import { distinctByLiteralProperty } from '@fmnts/core';
import { RequirementType } from '../api-model/api-enums';

/**
 * Requirements consist of a general type of requirement,
 * and an optional configuration for that type.
 */
export interface Requirement {
  /**
   * Identifier for the type of requirement.
   *
   * @see {@link RequirementType}
   */
  require: RequirementType;
  /**
   * A configuration for the requirement.
   * The exact type depends on the type of the requirement.
   */
  config?: unknown;
}

/**
 * Photo verification requirement.
 */
export interface PhotoVerifyRequirement extends Requirement {
  require: RequirementType.PhotoVerify;
}

/**
 * Location selection requirement.
 */
export interface LocationSelectRequirement extends Requirement {
  require: RequirementType.LocationSelect;
}

/**
 * Quiz requirement.
 */
export interface QuizRequirement extends Requirement {
  require: RequirementType.Quiz;
  config: {
    /** ID to the quiz that must have been passed. */
    quiz: string;
  };
}

/**
 * Type guards for requirement types.
 * This is wrapped in an anomynous function because we won't need the
 * helper function to create the distinctors afterwards.
 */
export const {
  [RequirementType.PhotoVerify]: isPhotoVerifyRequirement,
  [RequirementType.LocationSelect]: isLocationSelectRequirement,
  [RequirementType.Quiz]: isQuizRequirement,
} = (function () {
  const distinctRequirement =
    distinctByLiteralProperty<Requirement>()('require');

  return {
    /**
     * Type guard for photo verify requirement
     */
    [RequirementType.PhotoVerify]: distinctRequirement<PhotoVerifyRequirement>(
      RequirementType.PhotoVerify,
    ),
    /**
     * Type guard for location select requirement
     */
    [RequirementType.LocationSelect]:
      distinctRequirement<LocationSelectRequirement>(
        RequirementType.LocationSelect,
      ),
    /**
     * Type guard for location select requirement
     */
    [RequirementType.Quiz]: distinctRequirement<QuizRequirement>(
      RequirementType.Quiz,
    ),
  };
})();
